import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import PriceYearSelector, {
  PriceYearNote,
} from "../../components/priceYearSelector";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../../components/loadingContainer";
import ContainerHeader from "../../components/containerHeader";
import SummaryAccordion from "../../components/summaryAccordion";
import useEcomodClasses from "../../hooks/useEcomodClasses";
import { useUIStore } from "../../stores/ui-store";
import { useShallow } from "zustand/shallow";
import { UIState } from "../../types/ui-types";
import useSupClasses from "../../hooks/useSupClasses";
import usePrices from "../../hooks/usePrices";
import {
  getChangeErrors,
  getDivideAmount,
  getMatchingReport,
  getMultiplyAmount,
  getReportingCycle,
} from "../../utils/summaryUtils";
import {
  ReportStatus,
  InternalReportStatusEnum,
} from "../../api/models/report.models";
import useAdminReports from "../../hooks/useAdminReports";
import useAdminReport from "../../hooks/useAdminReport";
import { Report } from "../../api/models/report.models";
import useSummaryData from "../../hooks/useSummaryData";
import useAdminCompanies from "../../hooks/useAdminCompanies";
import TitleComponent from "../../components/title";
import useAdminReportCommands from "../../hooks/useAdminReportCommands";
import moment from "moment";
import { Button } from "primereact/button";
import { useAdminDataContext } from "../../contexts/adminDataContext";
import useComments from "../../hooks/useComments";
import CommentsSection from "../../components/commentsSection";

const PaddedContainer = styled.div`
  padding: 6rem 3rem;
  text-align: left;
  flex-grow: 1;
`;

const ReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  width: 100%;
`;

const CommentSection = styled.div`
  display: flex;
  gap: 2rem;
  padding: 0 1rem;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const CommentContent = styled.div`
  flex: 1;

  & h4 {
    font-size: 18px;
    font-weight: 600;
  }
`;

const ReviewSection = styled.div`
  flex: 0 0 550px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e1e1e1;
  margin-top: auto;

  @media (max-width: 768px) {
    flex: 1;
    width: 100%;
  }
`;

const ReviewItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
`;

const Label = styled.div`
  font-weight: 600;
  color: var(--text-color);
  font-size: 0.9rem;
`;

const StatusBadge = styled.div<{ $status?: InternalReportStatusEnum }>`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  background-color: #fff;
  border: 1px solid #e1e1e1;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-direction: row;
  margin-top: 0.5rem;
  justify-content: space-between;
`;

const StatusRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 4rem 1rem;
`;

const TotalCostContainer = styled.div`
  margin-top: 2rem;
  padding-right: 1rem;
  font-size: 18px;
  font-weight: 600;
  width: 100%;
  text-align: right;
  display: flex;
  flex-direction: column;

  & .notification {
    font-size: 14px;
    color: var(--text-color-secondary);
    font-weight: 500;
    margin-top: 0.5rem;
  }
`;

const NoDataPadding = styled.div`
  padding-left: 2rem;
`;

const TotalCostRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const ChangeErrorContainer = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background-color: #ffebee;
  border: 1px solid #f44336;
  border-radius: 4px;

  & h4 {
    margin-top: 0;
    font-size: 16px;
    color: #d32f2f;
  }

  & ul {
    margin: 0.5rem 0 0 1.5rem;
    padding-left: 0;
    color: #d32f2f;

    & li {
      margin-bottom: 0.25rem;
    }
  }
`;

const AdminReportView: React.FC = () => {
  const { t } = useTranslation();
  const { reportId } = useParams();
  const [locale] = useUIStore(useShallow((state: UIState) => [state.locale]));

  const { users, isLoadingUsers } = useAdminDataContext();

  const {
    updateReportStatus,
    updateInternalStatus,
    isUpdatingReportStatus,
    isUpdatingInternalReportStatus,
  } = useAdminReportCommands();

  const { comments, isLoadingComments, addComment, isAddingComment } =
    useComments({ reportId: parseInt(reportId as string, 10) });

  const { companies, isLoadingCompanies } = useAdminCompanies({
    includeOrganizations: false,
  });

  const { reportData, isLoadingReport } = useAdminReport({
    reportId: parseInt(reportId as string, 10),
    disableCache: true,
  });

  const { reportsData, isLoadingReports } = useAdminReports({
    companyId: reportData?.companyId || null,
    organizationId: reportData?.organizationId,
  });

  const company = companies?.find((comp) => comp.id === reportData?.companyId);
  const organization = company?.organizations.find(
    (org) => org.id === reportData?.organizationId,
  );

  // Find all reports from previous year, they might be yearly or quarterly
  const prevYearReports = reportData?.year
    ? reportsData?.filter(
        (report) =>
          report.year === reportData?.year - 1 &&
          report.reportStatus === ReportStatus.Sent &&
          report.id !== reportData.id,
      )
    : null;

  // Get previous year same quarter report or yearly report
  const prevYearReport = getMatchingReport(prevYearReports, reportData);

  const [priceYearOverride, setPriceYearOverride] = useState<number | null>(
    null,
  );

  const currentYear = new Date().getFullYear();
  const baseYear = reportData?.year ?? currentYear;
  const priceYearOptions = Array.from(
    { length: currentYear + 1 - baseYear + 1 },
    (_, i) => ({ label: String(baseYear + i), value: baseYear + i }),
  );

  const { pricesData, isLoading: isLoadingPrices } = usePrices({
    year: priceYearOverride ?? (reportData ? reportData.year : currentYear),
  });

  const { ecomodClasses, isLoading: isLoadingEcomodClasses } =
    useEcomodClasses();

  const { supClasses, isLoading: isLoadingSupClasses } = useSupClasses();

  const {
    consumerData,
    prevYearConsumerData,
    businessData,
    prevYearBusinessData,
    supData,
    prevYearSupData,
    totalCost,
  } = useSummaryData({
    reportData,
    prevYearReport,
    ecomodClasses,
    supClasses,
    pricesData,
    locale,
  });

  const currentYearReportingCycle = getReportingCycle(reportData as Report);

  const prevYearReportingCycle = getReportingCycle(prevYearReport as Report);

  const dividePreviousYearBy = getDivideAmount(
    prevYearReportingCycle,
    currentYearReportingCycle,
  );

  const multiplyPreviousYearBy = getMultiplyAmount(
    prevYearReportingCycle,
    currentYearReportingCycle,
  );

  const changeErrors = useMemo(() => {
    return getChangeErrors(
      consumerData || [],
      dividePreviousYearBy,
      multiplyPreviousYearBy,
      prevYearConsumerData,
      t("label-consumer"),
    )
      .concat(
        getChangeErrors(
          businessData || [],
          dividePreviousYearBy,
          multiplyPreviousYearBy,
          prevYearBusinessData,
          t("label-business"),
        ),
      )
      .concat(
        getChangeErrors(
          supData || [],
          dividePreviousYearBy,
          multiplyPreviousYearBy,
          prevYearSupData,
          t("label-single-use-plastic"),
        ),
      );
  }, [
    consumerData,
    prevYearConsumerData,
    businessData,
    prevYearBusinessData,
    supData,
    prevYearSupData,
    dividePreviousYearBy,
    multiplyPreviousYearBy,
    t,
  ]);

  const isLoading =
    isLoadingReport ||
    isLoadingEcomodClasses ||
    isLoadingSupClasses ||
    isLoadingPrices ||
    isLoadingCompanies ||
    isLoadingReports ||
    isLoadingUsers;

  const getStatusText = (status?: InternalReportStatusEnum) => {
    switch (status) {
      case InternalReportStatusEnum.ToBeReviewed:
        return t("label-to-be-reviewed");
      case InternalReportStatusEnum.ReturnedForCorrection:
        return t("label-returned-for-correction");
      case InternalReportStatusEnum.ApprovedAutomatically:
        return t("label-approved-automatically");
      case InternalReportStatusEnum.ApprovedManually:
        return t("label-approved-manually");
      default:
        return t("label-no-status");
    }
  };

  const handleApprove = async () => {
    if (!reportData) return;

    await updateInternalStatus({
      id: reportData.id,
      internalStatus: InternalReportStatusEnum.ApprovedManually,
    });
  };

  const handleReturnForCorrection = async () => {
    if (!reportData) return;

    await updateReportStatus({
      id: reportData.id,
      status: ReportStatus.Draft,
    });
  };

  const approvedByUser = reportData?.approvedBy
    ? users?.find((user) => user.id === reportData.approvedBy)
    : undefined;

  return (
    <ReportContainer className="report-container">
      <LoadingContainer
        status={
          isLoading ? HttpRequestStatus.LOADING : HttpRequestStatus.SUCCESS
        }
      >
        <ContainerHeader title="" description="" />
        <TitleComponent
          text={
            t("header-report") +
            " " +
            reportData?.year +
            (reportData?.quarter ? " Q" + reportData?.quarter : "") +
            " - " +
            (organization
              ? organization.organizationName
              : company?.companyName)
          }
        />
        {reportData?.rows.length === 0 && (
          <PaddedContainer>
            <NoDataPadding>{t("text-no-data-in-report")}</NoDataPadding>
          </PaddedContainer>
        )}
        <PaddedContainer>
          {reportData?.comment && (
            <CommentSection>
              <CommentContent>
                <h4>{t("header-customer-comments")}</h4>
                <p>{reportData.comment}</p>
                {changeErrors.length > 0 && (
                  <ChangeErrorContainer>
                    <h4>{t("header-changes-detected")}</h4>
                    <ul>
                      {changeErrors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </ChangeErrorContainer>
                )}
              </CommentContent>
              <ReviewSection>
                <StatusRow>
                  <ReviewItem>
                    <Label>{t("label-current-status")}</Label>
                    <StatusBadge $status={reportData.internalReportStatus}>
                      {getStatusText(reportData.internalReportStatus)}
                    </StatusBadge>
                  </ReviewItem>

                  {reportData.approvedAt && reportData.approvedBy && (
                    <ReviewItem>
                      <Label>{t("label-approved-by")}</Label>
                      <StatusBadge>
                        {approvedByUser?.names?.firstName +
                          " " +
                          approvedByUser?.names?.lastName +
                          " " +
                          moment(reportData.approvedAt).format(
                            "DD.MM.YYYY HH:mm",
                          )}
                      </StatusBadge>
                    </ReviewItem>
                  )}
                </StatusRow>

                <ButtonContainer>
                  <Button
                    label={t("cmd-return-for-correction")}
                    severity="warning"
                    onClick={handleReturnForCorrection}
                    icon="pi pi-reply"
                    loading={
                      isUpdatingReportStatus || isUpdatingInternalReportStatus
                    }
                    disabled={
                      reportData.internalReportStatus ===
                      InternalReportStatusEnum.ApprovedManually
                    }
                  />
                  <Button
                    label={t("cmd-approve")}
                    severity="success"
                    onClick={handleApprove}
                    icon="pi pi-check"
                    loading={
                      isUpdatingReportStatus || isUpdatingInternalReportStatus
                    }
                    disabled={
                      reportData.internalReportStatus ===
                      InternalReportStatusEnum.ApprovedManually
                    }
                  />
                </ButtonContainer>
              </ReviewSection>
            </CommentSection>
          )}

          {/* Comments/Messages Section */}
          <CommentsSection
            comments={comments}
            reportId={parseInt(reportId as string, 10)}
            isLoading={isLoadingComments}
            onAddComment={addComment}
            isAddingComment={isAddingComment}
            mode="accordion"
            accordionHeader={`${t("header-internal-comments")} ${comments && comments.length > 0 ? `(${comments.length})` : ""}`}
            initiallyExpanded={false}
            maxHeight="300px"
          />

          {reportData?.comment && <Divider />}
          {!!consumerData?.length &&
            consumerData.some((row) => row.combinedWeight > 0) && (
              <SummaryAccordion
                tableData={consumerData || []}
                prevYearData={prevYearConsumerData}
                prevYearReportingCycle={prevYearReportingCycle}
                currentYearReportingCycle={currentYearReportingCycle}
                header={t("label-consumer")}
              />
            )}
          {!!businessData?.length &&
            businessData.some((row) => row.combinedWeight > 0) && (
              <SummaryAccordion
                tableData={businessData || []}
                prevYearData={prevYearBusinessData}
                prevYearReportingCycle={prevYearReportingCycle}
                currentYearReportingCycle={currentYearReportingCycle}
                header={t("label-business")}
              />
            )}
          {!!supData?.length &&
            supData.some((row) => row.combinedWeight > 0) && (
              <SummaryAccordion
                tableData={supData || []}
                prevYearData={prevYearSupData}
                prevYearReportingCycle={prevYearReportingCycle}
                currentYearReportingCycle={currentYearReportingCycle}
                header={t("label-single-use-plastic")}
              />
            )}

          <TotalCostContainer>
            <TotalCostRow>
              <span>
                {`${t("label-total-cost")}: ${totalCost} ${totalCost ? "€" : ""}`}
                {`${totalCost ? ` ${t("label-vat-amount")}` : ""}`}
                {priceYearOverride && (
                  <PriceYearNote>
                    {`(${priceYearOverride} ${t("label-prices-from-year")})`}
                  </PriceYearNote>
                )}
              </span>
              <PriceYearSelector
                priceYearOverride={priceYearOverride}
                setPriceYearOverride={setPriceYearOverride}
                priceYearOptions={priceYearOptions}
                defaultYear={baseYear}
              />
            </TotalCostRow>
            <div className="notification">
              {t("label-total-amount-notification")}
            </div>
          </TotalCostContainer>
        </PaddedContainer>
      </LoadingContainer>
    </ReportContainer>
  );
};

export default AdminReportView;
